import type { FC } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from '@shared/core';
import type { BoxProps } from '@withjoy/joykit';
import { Box, Flex, LinkV2, TextV2 } from '@withjoy/joykit';
import { styles } from './BrandCarousel.styles';
import { useProductCatalogAdsQuery } from './graphql/getCatalogAds.graphql.generated';
import { usePaidAdsForShopTelemetry } from './BrandCarousel.telemetry';
import { Skeleton } from '@shared/components/Skeleton';
import { pxToRem } from '@withjoy/joykit/theme';
import { useCatalogRouting } from '@apps/registry/common/components/Catalog/useCatalogRouting';
import { boxShadowValuesV2 } from '@shared/utils/style/dropShadows';

export type BrandCarouselProps = {
  containerProps?: BoxProps;
  showAllBrandsLink?: boolean;
  onBrandClick?: (brandName: string) => void;
  onAllBrandsClick?: () => void;
};

type BrandData = {
  __typename?: 'ProductCatalogBrandPromo';
  customRedirectUrl?: string | null;
  brand: { __typename?: 'ProductCatalogBrand'; id: string; name: string; publicUrlPath: string };
  image: { __typename?: 'Photo'; id: string; url: string };
};

const columnSize = {
  first: [0, 0, 0, 4, 2],
  second: [12, 8, 8, 4, 6],
  third: [0, 4, 4, 4, 4],
};

const CarouselItem: FC<{ brandData: BrandData; onBrandClick?: BrandCarouselProps['onBrandClick'] }> = ({ brandData, onBrandClick }) => {
  const { image, brand, customRedirectUrl } = brandData;
  const { name, publicUrlPath } = brand;
  const telemetry = usePaidAdsForShopTelemetry();
  const { goToBrandPage } = useCatalogRouting();

  const handleCardClick = useCallback(() => {
    onBrandClick?.(name);
    telemetry.brandCardClicked({ brandName: name, cardType: 'brandPromoMosaic' });
    if (customRedirectUrl) {
      window.open(customRedirectUrl, '_blank');
    } else {
      goToBrandPage(publicUrlPath);
    }
  }, [name, telemetry, publicUrlPath, customRedirectUrl, goToBrandPage, onBrandClick]);

  return (
    <Flex
      onClick={handleCardClick}
      as="a"
      flex="1"
      flexDirection="column"
    >
      <Box
        borderRadius={3}
        background={`url(${image?.url}) lightgray 0px -302.5px / 100% 191.361% no-repeat`}
        boxShadow={boxShadowValuesV2.xxxl}
        backgroundSize="cover"
        backgroundPosition="center"
        width="100%"
        flex="1"
      />
      <TextV2
        marginTop={3}
        typographyVariant="button1"
      >
        {name}
      </TextV2>
    </Flex>
  );
};

export const BrandCarousel: FC<BrandCarouselProps> = ({ containerProps = {}, showAllBrandsLink = true, onAllBrandsClick, onBrandClick }) => {
  const { data, loading } = useProductCatalogAdsQuery();
  const { goToRootBrands } = useCatalogRouting();

  const tiles = data?.productCatalogAds?.brandPromoMosaic?.tiles;
  const { t } = useTranslation('yourRegistry');
  const { brandCarouselTitle, brandCarouselLink } = t('paidAds');
  const handleAllBrandsClick = useCallback(() => {
    onAllBrandsClick?.();
    goToRootBrands();
  }, [goToRootBrands, onAllBrandsClick]);

  if (loading) {
    return (
      <Skeleton
        height={pxToRem(465)}
        width="100%"
        borderRadius={3}
        {...containerProps}
      />
    );
  }

  if (!tiles?.length) return <></>;

  return (
    <Box
      width="100%"
      {...containerProps}
    >
      <Flex
        marginBottom={5}
        justifyContent="space-between"
        gap={5}
        alignItems="center"
      >
        <TextV2 typographyVariant={{ _: 'hed4', md: 'hed6' }}>{brandCarouselTitle()}</TextV2>
        {showAllBrandsLink && (
          <LinkV2
            isExternal
            onClick={handleAllBrandsClick}
            typographyVariant="button1"
            color={'black'}
          >
            {brandCarouselLink()}
          </LinkV2>
        )}
      </Flex>
      <Box
        width="100%"
        overflow="auto"
        overflowY="hidden"
      >
        <Box
          key={tiles[0]?.brand?.id}
          __css={styles.gridContainer}
        >
          {tiles?.length > 3 && (
            <Flex
              gap={5}
              flexDirection="column"
              gridColumn={`span ${columnSize.first[tiles?.length - 1]}`}
            >
              <CarouselItem
                brandData={tiles[3]}
                onBrandClick={onBrandClick}
              />
              {tiles[4] && (
                <CarouselItem
                  brandData={tiles[4]}
                  onBrandClick={onBrandClick}
                />
              )}
            </Flex>
          )}
          <Flex gridColumn={`span ${columnSize.second[tiles?.length - 1]}`}>
            <CarouselItem
              brandData={tiles[0]}
              onBrandClick={onBrandClick}
            />
          </Flex>
          {tiles?.length > 1 && (
            <Flex
              gap={5}
              flexDirection="column"
              gridColumn={`span ${columnSize.third[tiles?.length - 1]}`}
            >
              <CarouselItem
                brandData={tiles[1]}
                onBrandClick={onBrandClick}
              />
              {tiles[2] && (
                <CarouselItem
                  brandData={tiles[2]}
                  onBrandClick={onBrandClick}
                />
              )}
            </Flex>
          )}
        </Box>
      </Box>
    </Box>
  );
};
export default BrandCarousel;
